import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Globe, Loader2, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { SaveBar } from '../../../components/design-system/admin/SaveBar';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import { DESTRUCTIVE_OUTLINE } from '../../../components/design-system/admin/styles';
import { FilterChip } from '../../../components/design-system/common/FilterChip';
import { SettingsField, SettingsHint, SettingsToggleRow } from '../../../components/design-system/common/SettingsPanel';
import { useShopperRequestsApi } from '../../../common/hooks/use-shopper-requests-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { upperKey } from './request-format';
import { KIND_ICONS } from './request-presentation';
import {
    WEEK_ORDER,
    actionEnabled,
    clampInt,
    editStore,
    newStore,
    nextStepsEnabled,
    patchAction,
    stableJson,
    storeLocated,
    storesForSave,
    toggleIn,
    unnamedStores,
    visitSettings,
} from './settings-form';
import {
    CONTACT_CHANNELS,
    NextStepSettings,
    REQUEST_KINDS,
    RequestKind,
    ShopperRequestSettings,
    VISIT_SLOTS,
    VendorStore,
} from './types';

const CURRENCIES = ['EUR', 'GBP', 'USD', 'CHF', 'CAD', 'MAD', 'AED', 'SAR'];
const DAYS_NUMERIC = [0, 1, 2, 3, 4, 5, 6];

/**
 * Settings → what shoppers can ask for at the end of a session (and on what
 * terms), and the stores they can visit or pick a sample up from. One Save
 * for both: they are one decision for the vendor.
 */
export function SettingsTab() {
    const { t } = useTranslation();
    const api = useShopperRequestsApi();
    const [loaded, setLoaded] = useState<ShopperRequestSettings | null>(null);
    const [failed, setFailed] = useState(false);
    const [nextSteps, setNextSteps] = useState<NextStepSettings>({});
    const [stores, setStores] = useState<VendorStore[]>([]);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);

    const adopt = (s: ShopperRequestSettings, keepNextSteps = false) => {
        setLoaded(s);
        if (!keepNextSteps) setNextSteps(s?.nextSteps ?? {});
        setStores(s?.stores ?? []);
    };

    const load = () => {
        setFailed(false);
        api.getSettings()
            .then((s) => adopt(s))
            .catch(() => setFailed(true));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nextStepsDirty = !!loaded && stableJson(nextSteps) !== stableJson(loaded.nextSteps ?? {});
    const storesDirty = !!loaded && stableJson(stores) !== stableJson(loaded.stores ?? []);
    const dirty = nextStepsDirty || storesDirty;
    const unnamed = unnamedStores(stores);

    const save = async () => {
        setSaving(true);
        try {
            const saved = await api.saveSettings({ nextSteps, stores: storesForSave(stores) });
            adopt(saved);
            toast.success(t('SHOPPER_REQUESTS.SETTINGS.SAVED'));
        } catch (err) {
            const body = await readApiError(err);
            toast.error(body.message ? String(body.message) : t('SHOPPER_REQUESTS.SETTINGS.SAVE_FAILED'));
        } finally {
            setSaving(false);
        }
    };

    const importStores = async () => {
        setImporting(true);
        try {
            const result = await api.importStores();
            // The store list comes back from the server; next-step edits not yet saved are kept.
            adopt(result, true);
            if (!result.found) toast(t('SHOPPER_REQUESTS.STORES.IMPORT_NONE'));
            else if (!result.added) toast(t('SHOPPER_REQUESTS.STORES.IMPORT_NOTHING_NEW'));
            else toast.success(t('SHOPPER_REQUESTS.STORES.IMPORT_DONE', { found: result.found, added: result.added }));
        } catch {
            toast.error(t('SHOPPER_REQUESTS.STORES.IMPORT_FAILED'));
        } finally {
            setImporting(false);
        }
    };

    if (failed) {
        return (
            <Card data-testid="sr-settings">
                <CardContent className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-muted-foreground">{t('SHOPPER_REQUESTS.SETTINGS.LOAD_FAILED')}</p>
                    <Button variant="outline" size="sm" onClick={load}>
                        {t('SHOPPER_REQUESTS.RETRY')}
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (!loaded) {
        return (
            <div className="flex flex-col gap-6" data-testid="sr-settings">
                <Skeleton className="h-96 rounded-xl" />
                <Skeleton className="h-48 rounded-xl" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6" data-testid="sr-settings">
            <NextStepsCard nextSteps={nextSteps} settings={loaded} onChange={setNextSteps} />
            <StoresCard
                stores={stores}
                onChange={setStores}
                websiteShowrooms={loaded.websiteShowrooms ?? 0}
                importing={importing}
                importBlocked={storesDirty}
                onImport={() => void importStores()}
            />
            <SaveBar
                dirty={dirty}
                saving={saving}
                disabled={!dirty || unnamed > 0}
                hint={unnamed > 0 ? t('SHOPPER_REQUESTS.SETTINGS.UNNAMED_HINT') : undefined}
                onSave={() => void save()}
                onDiscard={() => adopt(loaded)}
                testId="sr-save-bar"
            />
        </div>
    );
}

// ── What shoppers can ask for ───────────────────────────────────────────

function NextStepsCard({
    nextSteps,
    settings,
    onChange,
}: {
    nextSteps: NextStepSettings;
    settings: ShopperRequestSettings;
    onChange: (next: NextStepSettings) => void;
}) {
    const { t } = useTranslation();
    const resolved = settings.resolved;
    const master = nextStepsEnabled(nextSteps, resolved);
    const channels = nextSteps.channels ?? resolved?.channels ?? [...CONTACT_CHANNELS];

    return (
        <SectionCard title={t('SHOPPER_REQUESTS.SETTINGS.ACTIONS_TITLE')} description={t('SHOPPER_REQUESTS.SETTINGS.ACTIONS_DESC')} testId="sr-next-steps">
            <div className="flex flex-col gap-6">
                <SettingsToggleRow
                    id="sr-master"
                    label={t('SHOPPER_REQUESTS.SETTINGS.MASTER')}
                    description={t('SHOPPER_REQUESTS.SETTINGS.MASTER_DESC')}
                    checked={master}
                    onCheckedChange={(v) => onChange({ ...nextSteps, enabled: v })}
                />

                <div className={cn('flex flex-col divide-y rounded-xl border', !master && 'opacity-60')}>
                    {REQUEST_KINDS.map((kind) => (
                        <ActionRow key={kind} kind={kind} nextSteps={nextSteps} settings={settings} disabled={!master} onChange={onChange} />
                    ))}
                </div>

                <div>
                    <p className="text-sm text-foreground">{t('SHOPPER_REQUESTS.SETTINGS.CHANNELS')}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t('SHOPPER_REQUESTS.SETTINGS.CHANNELS_DESC')}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {CONTACT_CHANNELS.map((c) => {
                            const on = channels.includes(c);
                            return (
                                <FilterChip
                                    key={c}
                                    size="md"
                                    selected={on}
                                    label={t(`SHOPPER_REQUESTS.CHANNEL.${upperKey(c)}`)}
                                    // One way of being answered stays: a form with none asks for nothing.
                                    onClick={() => (on && channels.length === 1 ? undefined : onChange({ ...nextSteps, channels: toggleIn(channels, c, CONTACT_CHANNELS) }))}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </SectionCard>
    );
}

function ActionRow({
    kind,
    nextSteps,
    settings,
    disabled,
    onChange,
}: {
    kind: RequestKind;
    nextSteps: NextStepSettings;
    settings: ShopperRequestSettings;
    disabled: boolean;
    onChange: (next: NextStepSettings) => void;
}) {
    const { t } = useTranslation();
    const Icon = KIND_ICONS[kind];
    const on = actionEnabled(nextSteps, settings.resolved, kind);
    const key = upperKey(kind);
    // Off only because there is nowhere to send the shopper yet: say what turns it on.
    const waitsForStore = !on && typeof nextSteps[kind]?.enabled !== 'boolean' && (kind === 'sample' || kind === 'visit') && !settings.stores?.length;

    return (
        <div className="p-4" data-testid={`sr-action-${kind}`}>
            <SettingsToggleRow
                id={`sr-action-${kind}`}
                icon={<Icon />}
                label={t(`SHOPPER_REQUESTS.SETTINGS.ACTION.${key}.LABEL`)}
                description={
                    <>
                        {t(`SHOPPER_REQUESTS.SETTINGS.ACTION.${key}.DESC`)}
                        {waitsForStore && <span className="mt-0.5 block">{t('SHOPPER_REQUESTS.SETTINGS.NEEDS_STORE')}</span>}
                    </>
                }
                checked={on}
                disabled={disabled}
                onCheckedChange={(v) => onChange(patchAction(nextSteps, kind, { enabled: v }))}
            >
                <SettingsField label={t('SHOPPER_REQUESTS.SETTINGS.NOTE')} htmlFor={`sr-note-${kind}`}>
                    <Input
                        id={`sr-note-${kind}`}
                        value={nextSteps[kind]?.note ?? ''}
                        maxLength={160}
                        placeholder={t(`SHOPPER_REQUESTS.SETTINGS.NOTE_PLACEHOLDER.${key}`)}
                        onChange={(e) => onChange(patchAction(nextSteps, kind, { note: e.target.value }))}
                        disabled={disabled}
                    />
                </SettingsField>
                {kind === 'sample' && <SampleFields nextSteps={nextSteps} disabled={disabled} onChange={onChange} />}
                {kind === 'visit' && <VisitFields nextSteps={nextSteps} settings={settings} disabled={disabled} onChange={onChange} />}
            </SettingsToggleRow>
        </div>
    );
}

function SampleFields({ nextSteps, disabled, onChange }: { nextSteps: NextStepSettings; disabled: boolean; onChange: (next: NextStepSettings) => void }) {
    const { t } = useTranslation();
    const sample = nextSteps.sample ?? {};
    const free = sample.price === 0;
    const currency = sample.currency || 'EUR';
    const currencies = CURRENCIES.includes(currency) ? CURRENCIES : [currency, ...CURRENCIES];

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
                <Label htmlFor="sr-sample-free" className="text-sm font-normal">
                    {t('SHOPPER_REQUESTS.SETTINGS.SAMPLE_FREE')}
                </Label>
                <Switch
                    id="sr-sample-free"
                    checked={free}
                    disabled={disabled}
                    onCheckedChange={(v) => onChange(patchAction(nextSteps, 'sample', { price: v ? 0 : null }))}
                />
            </div>
            {!free && (
                <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                    <SettingsField label={t('SHOPPER_REQUESTS.SETTINGS.SAMPLE_PRICE')} htmlFor="sr-sample-price" hint={t('SHOPPER_REQUESTS.SETTINGS.SAMPLE_PRICE_HINT')}>
                        <Input
                            id="sr-sample-price"
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="0.01"
                            value={typeof sample.price === 'number' ? String(sample.price) : ''}
                            disabled={disabled}
                            onChange={(e) => {
                                const raw = e.target.value;
                                const n = Number(raw);
                                onChange(patchAction(nextSteps, 'sample', { price: raw === '' || !Number.isFinite(n) || n < 0 ? null : n, currency }));
                            }}
                        />
                    </SettingsField>
                    <SettingsField label={t('SHOPPER_REQUESTS.SETTINGS.CURRENCY')} htmlFor="sr-sample-currency">
                        <NativeSelect
                            id="sr-sample-currency"
                            value={currency}
                            disabled={disabled}
                            onChange={(e) => onChange(patchAction(nextSteps, 'sample', { currency: e.target.value }))}
                        >
                            {currencies.map((c) => (
                                <NativeSelectOption key={c} value={c}>
                                    {c}
                                </NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </SettingsField>
                </div>
            )}
        </div>
    );
}

function VisitFields({
    nextSteps,
    settings,
    disabled,
    onChange,
}: {
    nextSteps: NextStepSettings;
    settings: ShopperRequestSettings;
    disabled: boolean;
    onChange: (next: NextStepSettings) => void;
}) {
    const { t, i18n } = useTranslation();
    const visit = visitSettings(nextSteps, settings.resolved);
    const weekday = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
        // 4 Jan 2026 was a Sunday: day n of that week is weekday n.
        return (d: number) => fmt.format(new Date(2026, 0, 4 + d));
    }, [i18n.language]);

    return (
        <div className="flex flex-col gap-4">
            <SettingsField label={t('SHOPPER_REQUESTS.SETTINGS.VISIT_DAYS')}>
                <div className="flex flex-wrap gap-1.5" data-testid="sr-visit-days">
                    {WEEK_ORDER.map((d) => (
                        <FilterChip
                            key={d}
                            selected={visit.days.includes(d)}
                            label={weekday(d)}
                            onClick={() => !disabled && onChange(patchAction(nextSteps, 'visit', { days: toggleIn(visit.days, d, DAYS_NUMERIC) }))}
                        />
                    ))}
                </div>
            </SettingsField>
            <SettingsField label={t('SHOPPER_REQUESTS.SETTINGS.VISIT_SLOTS')}>
                <div className="flex flex-wrap gap-1.5">
                    {VISIT_SLOTS.map((s) => (
                        <FilterChip
                            key={s}
                            selected={visit.slots.includes(s)}
                            label={t(`SHOPPER_REQUESTS.SLOT.${upperKey(s)}`)}
                            onClick={() => !disabled && onChange(patchAction(nextSteps, 'visit', { slots: toggleIn(visit.slots, s, VISIT_SLOTS) }))}
                        />
                    ))}
                </div>
            </SettingsField>
            <div className="grid gap-3 sm:grid-cols-2">
                <SettingsField label={t('SHOPPER_REQUESTS.SETTINGS.LEAD_DAYS')} htmlFor="sr-lead-days" hint={t('SHOPPER_REQUESTS.SETTINGS.LEAD_DAYS_HINT')}>
                    <Input
                        id="sr-lead-days"
                        type="number"
                        min={0}
                        max={30}
                        value={String(visit.leadDays)}
                        disabled={disabled}
                        onChange={(e) => onChange(patchAction(nextSteps, 'visit', { leadDays: clampInt(e.target.value, 0, 30, visit.leadDays) }))}
                    />
                </SettingsField>
                <SettingsField label={t('SHOPPER_REQUESTS.SETTINGS.HORIZON_DAYS')} htmlFor="sr-horizon-days" hint={t('SHOPPER_REQUESTS.SETTINGS.HORIZON_DAYS_HINT')}>
                    <Input
                        id="sr-horizon-days"
                        type="number"
                        min={1}
                        max={120}
                        value={String(visit.horizonDays)}
                        disabled={disabled}
                        onChange={(e) => onChange(patchAction(nextSteps, 'visit', { horizonDays: clampInt(e.target.value, 1, 120, visit.horizonDays) }))}
                    />
                </SettingsField>
            </div>
        </div>
    );
}

// ── Your stores ─────────────────────────────────────────────────────────

function StoresCard({
    stores,
    onChange,
    websiteShowrooms,
    importing,
    importBlocked,
    onImport,
}: {
    stores: VendorStore[];
    onChange: (next: VendorStore[]) => void;
    websiteShowrooms: number;
    importing: boolean;
    /** Unsaved store edits: importing would replace them with the saved list. */
    importBlocked: boolean;
    onImport: () => void;
}) {
    const { t } = useTranslation();
    const set = (i: number, field: keyof VendorStore, value: any) => onChange(stores.map((s, j) => (j === i ? editStore(s, field, value) : s)));
    const remove = (i: number) => onChange(stores.filter((_, j) => j !== i));

    return (
        <SectionCard
            title={t('SHOPPER_REQUESTS.STORES.TITLE')}
            description={t('SHOPPER_REQUESTS.STORES.DESC')}
            testId="sr-stores"
            action={
                <Button variant="outline" size="sm" onClick={() => onChange([...stores, newStore(stores)])} data-testid="sr-add-store">
                    <Plus />
                    {t('SHOPPER_REQUESTS.STORES.ADD')}
                </Button>
            }
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <p className="text-sm">{t('SHOPPER_REQUESTS.STORES.IMPORT_TITLE')}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            {websiteShowrooms > 0
                                ? t('SHOPPER_REQUESTS.STORES.WEBSITE_COUNT', { count: websiteShowrooms })
                                : t('SHOPPER_REQUESTS.STORES.WEBSITE_NONE')}
                        </p>
                        {importBlocked && <SettingsHint>{t('SHOPPER_REQUESTS.STORES.IMPORT_SAVE_FIRST')}</SettingsHint>}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onImport}
                        disabled={importing || importBlocked}
                        className="shrink-0 self-start sm:self-center"
                        data-testid="sr-import-stores"
                    >
                        {importing ? <Loader2 className="animate-spin" /> : <Globe />}
                        {t('SHOPPER_REQUESTS.STORES.IMPORT')}
                    </Button>
                </div>

                {stores.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">{t('SHOPPER_REQUESTS.STORES.EMPTY')}</p>
                ) : (
                    stores.map((store, i) => (
                        <StoreEditor key={store.id || i} store={store} index={i} onField={(f, v) => set(i, f, v)} onRemove={() => remove(i)} />
                    ))
                )}
            </div>
        </SectionCard>
    );
}

function StoreEditor({
    store,
    index,
    onField,
    onRemove,
}: {
    store: VendorStore;
    index: number;
    onField: (field: keyof VendorStore, value: any) => void;
    onRemove: () => void;
}) {
    const { t } = useTranslation();
    const located = storeLocated(store);
    const id = (field: string) => `sr-store-${index}-${field}`;
    const text = (field: keyof VendorStore, label: string, extra: Partial<React.ComponentProps<typeof Input>> = {}, className?: string, hint?: string) => (
        <SettingsField label={label} htmlFor={id(field)} className={className} hint={hint}>
            <Input id={id(field)} value={(store[field] as string) ?? ''} onChange={(e) => onField(field, e.target.value)} {...extra} />
        </SettingsField>
    );

    return (
        <div className="flex flex-col gap-4 rounded-xl border p-4" data-testid="sr-store">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                    <span className="truncate text-sm font-medium">{store.name?.trim() || t('SHOPPER_REQUESTS.STORES.UNTITLED')}</span>
                    <StatusBadge tone={located ? 'success' : 'neutral'} className="text-muted-foreground">
                        {located ? t('SHOPPER_REQUESTS.STORES.LOCATED') : t('SHOPPER_REQUESTS.STORES.NOT_LOCATED')}
                    </StatusBadge>
                </div>
                <Button variant="outline" size="sm" className={DESTRUCTIVE_OUTLINE} onClick={onRemove}>
                    <Trash2 />
                    {t('SHOPPER_REQUESTS.STORES.REMOVE')}
                </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-6">
                {text('name', t('SHOPPER_REQUESTS.STORES.FIELD.NAME'), { maxLength: 120 }, 'sm:col-span-6')}
                {text('address', t('SHOPPER_REQUESTS.STORES.FIELD.ADDRESS'), { maxLength: 240 }, 'sm:col-span-6', located ? undefined : t('SHOPPER_REQUESTS.STORES.NOT_LOCATED_HINT'))}
                {text('postcode', t('SHOPPER_REQUESTS.STORES.FIELD.POSTCODE'), { maxLength: 20 }, 'sm:col-span-2')}
                {text('city', t('SHOPPER_REQUESTS.STORES.FIELD.CITY'), { maxLength: 80 }, 'sm:col-span-3')}
                {text('country', t('SHOPPER_REQUESTS.STORES.FIELD.COUNTRY'), { maxLength: 2, placeholder: 'FR', className: 'uppercase' }, 'sm:col-span-1')}
                {text('phone', t('SHOPPER_REQUESTS.STORES.FIELD.PHONE'), { maxLength: 40, type: 'tel', dir: 'ltr' }, 'sm:col-span-3')}
                {text('email', t('SHOPPER_REQUESTS.STORES.FIELD.EMAIL'), { maxLength: 160, type: 'email', dir: 'ltr' }, 'sm:col-span-3')}
                {text('hours', t('SHOPPER_REQUESTS.STORES.FIELD.HOURS'), { maxLength: 240, placeholder: t('SHOPPER_REQUESTS.STORES.HOURS_PLACEHOLDER') }, 'sm:col-span-3')}
                {text('bookingUrl', t('SHOPPER_REQUESTS.STORES.FIELD.BOOKING'), { type: 'url', dir: 'ltr', placeholder: 'https://' }, 'sm:col-span-3')}
            </div>

            <div className="flex items-center justify-between gap-4">
                <Label htmlFor={id('sample-desk')} className="text-sm font-normal">
                    {t('SHOPPER_REQUESTS.STORES.SAMPLE_DESK')}
                </Label>
                <Switch id={id('sample-desk')} checked={!!store.sampleDesk} onCheckedChange={(v) => onField('sampleDesk', v)} />
            </div>
        </div>
    );
}
